import { DatePipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { WebStorage } from 'src/app/demo/components/auth/storage/web.storage';
import { AllInvoiceCount } from 'src/app/demo/model/AllInvoiceCount';
import { LookupService } from 'src/app/demo/service/lookup.service';
import { Common } from 'src/app/demo/utils/common';

@Component({
    selector: 'app-billing-overview-chart',
    templateUrl: './billing-overview-chart.component.html',
    styleUrls: ['./billing-overview-chart.component.scss'],
})
export class BillingOverviewChartComponent {
    @ViewChild('chart') chartRef!: ElementRef;
    @Input() hideCalendar: boolean = false;
    @Input() selectedMonth!: string;
    @Input() refreshKey!: number;

    @Input() startDate?: any;
    @Input() endDate?: any;
    @Input() selectedCardType?: string;

    @Output() cardClicked = new EventEmitter<string>();
    chartOptions: any;
    chartData: any;
    selectedYear: number = new Date().getFullYear();
    yearOptions!: any;
    monthOptions!: any;
    search: any = {};
    userData: any;
    invoiceCount: AllInvoiceCount = {
        amountPaid: 0,
        amountInProcess: 0,
        amountUnpaid: 0,
        totalAmountInvoiced: 0,
        amountRefund: 0,
    };
    paidColor: string = '#25ac7c';
    inProgressColor: string = '#FFDD00';
    outstandingColor: string = '#F38D8D';
    refundColor: string = '#A8D8FF';
    netPaidColour: string = '#ACF3AA';
    selectedMonth1: any;
    totalAmount: any;
    netPaid: number=0;

    /********Update********/
    outstandingAmount: number = 0;
    showOutstandingBox: boolean = false;
    /********Update********/

    monthsList = [
        { label: 'January', value: 1 },
        { label: 'February', value: 2 },
        { label: 'March', value: 3 },
        { label: 'April', value: 4 },
        { label: 'May', value: 5 },
        { label: 'June', value: 6 },
        { label: 'July', value: 7 },
        { label: 'August', value: 8 },
        { label: 'September', value: 9 },
        { label: 'October', value: 10 },
        { label: 'November', value: 11 },
        { label: 'December', value: 12 },
    ];
    constructor(
        private cdr: ChangeDetectorRef,
        private lookupService: LookupService,
        private storage: WebStorage,
        private datePipe: DatePipe,

    ) {
        const currentMonth = new Date().getMonth() + 1;
        let monthName = this.monthsList.find(
            (month) => month.value === currentMonth
        )?.label;
        this.selectedMonth1 = monthName;
    }
    /********Update********/
    ngOnInit() {
        this.userData = this.storage.getUserData();
        this.fetchBillingCardData(this.selectedMonth1);
        this.fetchOutstandingAmount();
        this.checkShowOutstandingBox();
        // console.log('showOutstandingBox:', this.showOutstandingBox, 'hash:', window.location.hash);
    }
    /********Update********/
    ngOnChanges() {
        this.userData = this.storage.getUserData();
        // Only fetch if both dates are present
        if (this.startDate && this.endDate) {
            this.fetchBillingCardData(this.selectedMonth1);
        }
        // Always check if we should show outstanding box
        this.checkShowOutstandingBox();
        // console.log('ngOnChanges - showOutstandingBox:', this.showOutstandingBox, 'hash:', window.location.hash);
    }

    onCardClick(type: string) {
        if (this.hideCalendar) { 
            this.cardClicked.emit(type);
        }
    }

    checkShowOutstandingBox() {
        // Show outstanding box when hideCalendar is true (all-transaction page)
        // or when URL hash matches
        this.showOutstandingBox = this.hideCalendar || 
            window.location.hash === '#/billing/alltransaction';
    }

    fetchOutstandingAmount() {
        const schoolId = this.userData?.schoolId;
        this.lookupService
            .querySchoolOutStdAmt({ schoolId })
            .subscribe((res: HttpResponse<any>) => {
                if (res.body && typeof res.body.amountUnpaid === 'number') {
                    this.outstandingAmount = res.body.amountUnpaid;
                } else {
                    this.outstandingAmount = 0;
                }
            });
    }

    /**********Update**********/
    fetchBillingCardData(event?: any) {
        this.selectedMonth1 = event;

        if (this.startDate && this.endDate) {
            const startDate = this.datePipe.transform(this.startDate, 'yyyy-MM-dd')!;
            const endDate = this.datePipe.transform(this.endDate, 'yyyy-MM-dd')!;
            this.search['date.greaterThanOrEqual'] =  Common.getUtcStartOfDay(startDate);
            this.search['date.lessThanOrEqual'] = Common.getUtcEndOfDay(endDate);
        } else {
            const boundaries = this.getMonthBoundaries(
                this.hideCalendar == false
                    ? this.selectedMonth1
                    : this.selectedMonth
            );
            
            // Convert the boundary dates to UTC using Common utility
            const { startUtc, endUtc } = Common.getUtcDateRange(
                boundaries.firstDay, // This is already in ISO format
                boundaries.lastDay   // This is already in ISO format
            );
            
            this.search['date.greaterThanOrEqual'] = startUtc;
            this.search['date.lessThanOrEqual'] = endUtc;
        }
        
        this.lookupService
            .queryAllInvoiceCount(this.search)
            .subscribe((res: HttpResponse<AllInvoiceCount>) => {
                this.invoiceCount = res.body
                    ? res.body
                    : {
                        amountPaid: 0,
                        amountInProcess: 0,
                        amountUnpaid: 0,
                        totalAmountInvoiced: 0,
                        amountRefund: 0,
                        netAmountPaid :0
                    };

                let amountPaid = res.body?.amountPaid ?? 0;
                let amountInProcess = res.body?.amountInProcess ?? 0;
                let amountUnpaid = res.body?.amountUnpaid ?? 0;
                let amountRefund = res.body?.amountRefund ?? 0;
                this.totalAmount = res.body?.totalAmountInvoiced ?? 0;
                this.netPaid = res.body?.netAmountPaid?? 0;
            });
    }

    getMonthBoundaries(monthName: string): {
        firstDay: string;
        lastDay: string;
    } {
        const year = new Date().getFullYear();
        const monthIndex = new Date(
            Date.parse(monthName + ' 1, ' + year)
        ).getMonth();

        // Create Date objects for first and last day of the month
        const firstDayDate = new Date(year, monthIndex, 1);
        const lastDayDate = new Date(year, monthIndex + 1, 0);

        // Convert to date-only strings
        const firstDayStr = firstDayDate.toISOString().split('T')[0];
        const lastDayStr = lastDayDate.toISOString().split('T')[0];

        // Use Common utility to get proper UTC boundaries
        const { startUtc, endUtc } = Common.getUtcDateRange(firstDayStr, lastDayStr);

        return {
            firstDay: startUtc,
            lastDay: endUtc,
        };
    }

    get activeProgressPercentage(): number {
        const paid = this.invoiceCount?.amountPaid ?? 0;
        const inProcess = this.invoiceCount?.amountInProcess ?? 0;
        const unpaid = this.invoiceCount?.amountUnpaid ?? 0;
        // const refund = this.invoiceCount?.amountRefund ?? 0;
        const total = paid + inProcess + unpaid ;
        return this.calculateProgressPercentage(paid, total);
    }

    get upcomingProgressPercentage(): number {
        const inProcess = this.invoiceCount?.amountInProcess ?? 0;
        const netPaid = this.invoiceCount?.netAmountPaid ?? 0;
        const unpaid = this.invoiceCount?.amountUnpaid ?? 0;
        const total = netPaid + inProcess + unpaid;
        return this.calculateProgressPercentage(inProcess, total);
    }

    get remainingProgressPercentage(): number {
        const inProcess = this.invoiceCount?.amountInProcess ?? 0;
        const netPaid = this.invoiceCount?.netAmountPaid ?? 0;
        const unpaid = this.invoiceCount?.amountUnpaid ?? 0;
        const total = netPaid + inProcess + unpaid;
        return this.calculateProgressPercentage(unpaid, total);
    }

    get refundProgressPercentage(): number {
        const paid = this.invoiceCount?.amountPaid ?? 0;
        const inProcess = this.invoiceCount?.amountInProcess ?? 0;
        const unpaid = this.invoiceCount?.amountUnpaid ?? 0;
        const refund = this.invoiceCount?.amountRefund ?? 0;
        const total = paid + inProcess + unpaid ;
        return this.calculateProgressPercentage(refund, total);
    }

    get netPaidProgressPercentage(): number {
        // const paid = this.invoiceCount?.amountPaid ?? 0;
        const inProcess = this.invoiceCount?.amountInProcess ?? 0;
        const unpaid = this.invoiceCount?.amountUnpaid ?? 0;
        // const refund = this.invoiceCount?.amountRefund ?? 0;
        const netPaid = this.invoiceCount?.netAmountPaid ?? 0;
        const total =   inProcess + unpaid + netPaid ;
        return this.calculateProgressPercentage(netPaid, total);
    }

    private calculateProgressPercentage(part: number, total: number): number {
        return Math.round(total ? (part / total) * 100 : 0);
    }
}
